import {useCallback, useRef, useState} from 'react';
import {useQueries, useQueryClient} from '@tanstack/react-query';

import apiService, {GetDepartmentLinesSortOptionId} from '../../transport/apiService';
import type {OrderLine} from '../../transport/apiServiceResponse';
import {getProductDisplayRules} from '../../utils/rewards';

import {departmentJoinKeys} from './keys';

const PAGE_SIZE = 48;
// SHORTCUT: Shop All member sets read one page of 200 lines. A department with more member
// lines at one tier shows the excess in top_products position. Upgrade: page the member sets.
const MEMBER_SET_PAGE_SIZE = 200;
// Ruling RC10: the department list's sort. Constant, but in the key so a future sort cannot collide.
const SORT = GetDepartmentLinesSortOptionId.TOP_PRODUCTS;

type Args = {
  storeId: string | null;
  departmentIds: string[];
  specials?: boolean;
  /** DIS-1907: the search bar's query; empty means no keyword filter. */
  keywords?: string;
  /** DIS-2007: with `specials`, also request `member_price=private`. Only for an entitled shopper. */
  includePrivate?: boolean;
};

type MemberPrice = 'public' | 'private';

type PageResponse = {data: OrderLine[]; meta?: {page: number; page_count: number}};

/**
 * Client-side join of several MFL departments: one query per
 * (department x page), merged page-major (page 1 of every department in
 * `departmentIds` order, then page 2, ...), deduped by line id.
 *
 * Without `specials` (a Shop All list), the member-set slots come first: page 1 of every
 * public set in `departmentIds` order, then page 1 of every private set. The plain streams
 * follow page-major. With `specials`, each department's member sets page beside its specials.
 */
export function useDepartmentJoinLines({
  storeId,
  departmentIds,
  specials,
  keywords,
  includePrivate,
}: Args) {
  const queryClient = useQueryClient();
  // DIS-2007: MFL scopes `specials=true` to the session, so a logged-in member's response omits
  // member-price lines. A specials join also reads each department's `member_price` sets.
  // DIS-2046: a Shop All list reads the same sets, so that member lines come first.
  const memberPrices: MemberPrice[] = includePrivate ? ['public', 'private'] : ['public'];
  const sources: (MemberPrice | undefined)[] = [undefined, ...memberPrices];
  const signature = `${storeId}|${departmentIds.join(',')}|${!!specials}|${keywords ?? ''}|${memberPrices.join(',')}`;
  // Derive-state-during-render reset: a store/department/specials/keywords change
  // replaces the stored pages with 1, so returning to an earlier signature
  // (A -> B -> A) does not resurrect its old page count.
  const [paged, setPaged] = useState({signature, pages: 1});
  if (paged.signature !== signature) setPaged({signature, pages: 1});
  const pages = paged.signature === signature ? paged.pages : 1;
  const wantSpecials = !!specials;
  // RC4b: a slot's first error, keyed by signature, department and page, kept until the slot
  // has data. A refetch of a slot with no data resets its `error` to null, and a new error
  // object from a failed refetch would make the screen warn again.
  const stickyErrors = useRef(new Map<string, Error>()).current;

  // A stream is one department's paging under one source: specials, or a member_price set.
  // The first `streams.length` slots are page 1 of every stream; `isLoading` reads those.
  const streams = specials
    ? departmentIds.flatMap(departmentId =>
        sources.map(memberPrice => ({departmentId, memberPrice})),
      )
    : [...memberPrices, undefined].flatMap(memberPrice =>
        departmentIds.map(departmentId => ({departmentId, memberPrice})),
      );
  // DIS-2046: a Shop All member set reads page 1 only and is not part of the list's paging.
  const isShopAllMemberSet = (memberPrice?: MemberPrice) => !specials && !!memberPrice;
  const slots: {departmentId: string; memberPrice?: MemberPrice; stream: string; page: number}[] =
    [];
  for (let page = 1; page <= pages; page++) {
    for (const {departmentId, memberPrice} of streams) {
      if (page > 1 && isShopAllMemberSet(memberPrice)) continue;
      slots.push({departmentId, memberPrice, stream: `${departmentId}|${memberPrice ?? ''}`, page});
    }
  }

  const results = useQueries({
    queries: slots.map(({departmentId, memberPrice, page}) => {
      const previous =
        page > 1
          ? queryClient.getQueryData<PageResponse>(
              departmentJoinKeys.page(
                storeId,
                departmentId,
                wantSpecials,
                page - 1,
                SORT,
                keywords,
                memberPrice,
              ),
            )
          : undefined;
      return {
        queryKey: departmentJoinKeys.page(
          storeId,
          departmentId,
          wantSpecials,
          page,
          SORT,
          keywords,
          memberPrice,
        ),
        queryFn: (): Promise<PageResponse> =>
          apiService.getDepartmentLines(
            {
              keywords: keywords || undefined,
              department_id: departmentId,
              sort_option_id: SORT,
              // A member_price set is its own source, not filtered by specials.
              specials: (!memberPrice && specials) || undefined,
              ...(memberPrice ? {member_price: memberPrice} : {}),
              page,
              page_size: isShopAllMemberSet(memberPrice) ? MEMBER_SET_PAGE_SIZE : PAGE_SIZE,
            },
            // A header department overrides the query department_id.
            {unscoped: true},
          ) as Promise<PageResponse>,
        // Failures must not hold back the other departments (RC5), so no
        // retry/backoff: a retrying page-1 query would stay pending.
        retry: false,
        enabled: !!storeId && (page === 1 || (previous?.meta?.page_count ?? 0) >= page),
        // A refetch could add a member line above tiles the shopper has seen.
        ...(isShopAllMemberSet(memberPrice)
          ? {refetchOnWindowFocus: false, refetchOnReconnect: false}
          : {}),
      };
    }),
  });

  const lines: OrderLine[] = [];
  const seen = new Set<string>();
  let error: Error | null = null;
  // Last page with data, per stream, for hasNextPage. A stream whose
  // later page errored cannot fetch a next page.
  const lastLoaded = new Map<string, PageResponse>();
  const failedAfter = new Set<string>();
  // Append only (RC4a): show lines up to the first slot still loading, so an earlier slot
  // that answers late never lands above lines already shown. `isLoading` is pending and
  // fetching, so a failed or disabled (exhausted) slot never blocks. RC4b: only a first load
  // blocks. A refetch of a slot with no data puts it back to pending, but `isFetched`
  // (dataUpdateCount + errorUpdateCount > 0) survives that refetch. DIS-2046: a Shop All member
  // set with no data always blocks while it fetches, because its late answer lands above the list.
  const blocks = (r: (typeof results)[number], i: number) =>
    r.isLoading && (!r.isFetched || isShopAllMemberSet(slots[i].memberPrice));
  const firstLoading = results.findIndex(blocks);
  const shown = firstLoading === -1 ? slots.length : firstLoading;
  // A plain loop, not forEach: assignments inside a callback are invisible to narrowing,
  // which typed the returned `error` as `null`.
  for (const [i, {memberPrice, stream, page}] of slots.entries()) {
    const result = results[i];
    const hash = `${signature}|${stream}|${page}`;
    if (result.error && !stickyErrors.has(hash)) stickyErrors.set(hash, result.error);
    else if (!result.error && result.data) stickyErrors.delete(hash);
    const slotError = stickyErrors.get(hash) ?? null;
    if (!error && slotError) error = slotError;
    if (!result.data) {
      if (slotError && !isShopAllMemberSet(memberPrice)) failedAfter.add(stream);
      continue;
    }
    if (!isShopAllMemberSet(memberPrice)) {
      lastLoaded.set(stream, result.data);
      failedAfter.delete(stream);
    }
    if (i >= shown) continue;
    for (const line of result.data.data ?? []) {
      if (seen.has(line.id)) continue;
      // As MembersPriceListScreen: a member_price set keeps only lines that present at that tier.
      if (memberPrice === 'public' && !getProductDisplayRules(line).presentAsPublicMemberProduct) {
        continue;
      }
      if (
        memberPrice === 'private' &&
        !getProductDisplayRules(line).presentAsPrivateMemberProduct
      ) {
        continue;
      }
      seen.add(line.id);
      lines.push(line);
    }
  }

  const isLoading =
    !!storeId &&
    streams.some(
      ({memberPrice}, i) =>
        (results[i].status === 'pending' && !results[i].isFetched) ||
        (isShopAllMemberSet(memberPrice) && results[i].isLoading),
    );

  // Forward fetches only: a background refetch of a loaded page is not a next page.
  const isFetchingNextPage =
    pages > 1 && slots.some(({page}, i) => page === pages && blocks(results[i], i));

  const hasNextPage = [...lastLoaded.keys()].some(stream => {
    const response = lastLoaded.get(stream);
    if (!response?.meta || failedAfter.has(stream)) return false;
    return response.meta.page_count > response.meta.page;
  });

  // DIS-2007: never request past the deepest page_count seen. A department whose page 1
  // answers after another department has loaded later pages keeps hasNextPage true while its
  // own next pages are still disabled, so isFetchingNextPage cannot stop a drain loop.
  const maxPageCount = Math.max(
    0,
    ...[...lastLoaded.values()].map(response => response.meta?.page_count ?? 0),
  );
  const fetchNextPage = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage || pages >= maxPageCount) return;
    setPaged({signature, pages: pages + 1});
  }, [hasNextPage, isFetchingNextPage, signature, pages, maxPageCount]);

  return {lines, isLoading, error, hasNextPage, isFetchingNextPage, fetchNextPage};
}
